// Attestation Dashboard — Liste des demandes d'attestation de l'utilisateur
// Statuts : en_attente (jaune), validee (vert + badge dore), refusee (rouge), generee (gris)

import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Loader2, FileText, ShieldCheck, AlertTriangle, Award } from 'lucide-react'
import { useNotifications } from '../contexts/NotificationsContext'
import { useEntreprise } from '../contexts/EntrepriseContext'
// Layout is handled by route-level DashboardLayout
import { Button } from '../components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { api } from '../services/api'

// Shape attendue des attestations retournees par l'API
interface Attestation {
  id: string
  diagnosticId: string
  entrepriseNom?: string
  nomEntreprise?: string
  entrepriseSiret?: string | null
  certificationType?: string
  casques?: number
  statut: 'en_attente' | 'validee' | 'refusee' | 'generee'
  createdAt: string
  dateValidite?: string
  refusedReason?: string | null
  validatedAt?: string | null
  entreprise?: { raisonSociale?: string; siret?: string | null }
  diagnostic?: {
    id: string
    entreprise?: { raisonSociale?: string; siret?: string | null }
    resultat?: { casques?: number }
  }
}

// Configuration des badges de statut
const STATUT_CONFIG: Record<
  Attestation['statut'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }
> = {
  en_attente: {
    label: 'En attente',
    variant: 'outline',
    className: 'border-yellow-400/60 bg-yellow-50 text-yellow-700',
  },
  validee: {
    label: 'Validée',
    variant: 'default',
    className: 'bg-amber-500 text-white',
  },
  refusee: {
    label: 'Refusée',
    variant: 'destructive',
    className: '',
  },
  generee: {
    label: 'Active',
    variant: 'default',
    className: 'bg-green-600 text-white',
  },
}

function StatutBadge({ statut }: { statut: Attestation['statut'] }) {
  const config = STATUT_CONFIG[statut]
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  )
}

function CasquesInline({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <img
          key={i}
          src={i < count ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
          alt={i < count ? 'Casque actif' : 'Casque inactif'}
          className="w-5 h-5"
        />
      ))}
    </div>
  )
}

function BadgeDore() {
  return (
    <img
      src="/images/medaille-dore.svg"
      alt="Badge doré"
      className="w-6 h-6 inline-block"
      title="Attestation validée — Badge doré"
    />
  )
}

export default function AttestationDashboardPage() {
  const navigate = useNavigate()
  const { acknowledgeAttestation } = useNotifications()
  const { currentEntreprise, currentSiret } = useEntreprise()
  const [allAttestations, setAllAttestations] = useState<Attestation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [lastDiagId, setLastDiagId] = useState<string | null>(null)

  // Filtrer par entreprise courante : SIRET en clé primaire, fallback raisonSociale.
  const attestations = useMemo(() => {
    if (!currentEntreprise) return allAttestations
    return allAttestations.filter((att) => {
      const siret = att.entreprise?.siret ?? att.diagnostic?.entreprise?.siret ?? att.entrepriseSiret ?? null
      const nom = att.entreprise?.raisonSociale ?? att.diagnostic?.entreprise?.raisonSociale ?? att.entrepriseNom ?? att.nomEntreprise ?? ''
      if (currentSiret && siret) return siret === currentSiret
      return nom === currentEntreprise
    })
  }, [allAttestations, currentEntreprise, currentSiret])

  // Marquer le badge attestation comme vu
  useEffect(() => {
    acknowledgeAttestation()
  }, [acknowledgeAttestation])

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const [attData, diagnostics] = await Promise.all([
          api.getUserAttestations(),
          api.getDiagnostics(),
        ])
        setAllAttestations(attData)
        // Trouver le dernier diagnostic terminé (avec résultat)
        const completed = diagnostics
          .filter((d: any) => d.resultat)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        if (completed.length > 0) {
          setLastDiagId(completed[0].id)
        }
      } catch (error) {
        console.error('Erreur chargement attestations:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])

  const handleDownloadPdf = async (diagnosticId: string) => {
    setDownloadingId(diagnosticId)
    try {
      await api.downloadAttestationPDF(diagnosticId)
    } catch (err) {
      console.error('Erreur telechargement PDF:', err)
    } finally {
      setDownloadingId(null)
    }
  }

  const isEmpty = attestations.length === 0

  return (
    <div>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Mes attestations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Suivez vos demandes d'attestation et téléchargez vos documents
            </p>
          </div>
          {lastDiagId && (
            <Button
              onClick={() => navigate(`/diagnostic/${lastDiagId}/attestation`)}
              className="shrink-0"
            >
              <Award className="size-4 mr-1.5" />
              Générer mon attestation
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : isEmpty ? (
          <EmptyState lastDiagId={lastDiagId} />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="size-4 text-muted-foreground" />
                Demandes d'attestation ({attestations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Type certif</TableHead>
                    <TableHead>Casques</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Validité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attestations.map((att) => {
                    const entreprise =
                      att.nomEntreprise ||
                      att.entrepriseNom ||
                      att.diagnostic?.entreprise?.raisonSociale ||
                      'Entreprise'
                    const casques =
                      att.casques ??
                      att.diagnostic?.resultat?.casques ??
                      0
                    const dateFormatted = new Date(att.createdAt).toLocaleDateString(
                      'fr-FR',
                      { day: 'numeric', month: 'short', year: 'numeric' }
                    )
                    const isValidee = att.statut === 'validee'
                    const diagId = att.diagnosticId || att.diagnostic?.id || ''
                    const canDownload = att.statut === 'generee' || att.statut === 'validee'

                    // Expiration
                    const dateValidite = att.dateValidite ? new Date(att.dateValidite) : null
                    const now = new Date()
                    const isExpired = dateValidite ? dateValidite < now : false
                    const daysUntilExpiry = dateValidite
                      ? Math.ceil((dateValidite.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                      : null
                    const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30
                    const validiteFormatted = dateValidite
                      ? dateValidite.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'

                    const isRefusee = att.statut === 'refusee'
                    const refusedReason = att.refusedReason?.trim() || null
                    const refusalDate = att.validatedAt
                      ? new Date(att.validatedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                      : null

                    return (
                      <Fragment key={att.id}>
                        <TableRow>
                          <TableCell className="font-medium">
                            {entreprise}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {att.certificationType || '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CasquesInline count={casques} />
                              {isValidee && <BadgeDore />}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {dateFormatted}
                          </TableCell>
                          <TableCell className="text-sm">
                            {isExpired ? (
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                <AlertTriangle className="size-3.5" />
                                Expirée
                              </span>
                            ) : isExpiringSoon ? (
                              <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                <AlertTriangle className="size-3.5" />
                                {validiteFormatted}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{validiteFormatted}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatutBadge statut={att.statut} />
                          </TableCell>
                          <TableCell className="text-right">
                            {canDownload && diagId && (
                              <button
                                onClick={() => handleDownloadPdf(diagId)}
                                disabled={downloadingId === diagId}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                                title="Télécharger le PDF"
                              >
                                {downloadingId === diagId ? (
                                  <Loader2 className="size-3.5 animate-spin" />
                                ) : (
                                  <Download className="size-3.5" />
                                )}
                                PDF
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                        {isRefusee && refusedReason && (
                          <TableRow className="bg-red-50/60 hover:bg-red-50/60">
                            <TableCell colSpan={7} className="py-3">
                              <div className="flex gap-3 items-start text-sm">
                                <AlertTriangle className="size-4 text-red-600 shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                  <div className="font-medium text-red-700">
                                    Motif du refus{refusalDate && <span className="text-red-600/70 font-normal"> · {refusalDate}</span>}
                                  </div>
                                  <p className="text-red-900/90 whitespace-pre-wrap">{refusedReason}</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
    </div>
  )
}

// --- Sous-composants ---

function EmptyState({ lastDiagId }: { lastDiagId: string | null }) {
  const navigate = useNavigate()
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
          <ShieldCheck className="size-8 text-muted-foreground/50" />
        </div>
        <h2 className="text-lg font-semibold mb-2">Aucune attestation</h2>
        <p className="text-muted-foreground leading-relaxed mb-6">
          {lastDiagId
            ? 'Votre diagnostic est terminé ! Générez votre attestation pour valoriser votre démarche de prévention.'
            : 'Vous n\'avez pas encore soumis de demande d\'attestation. Terminez un diagnostic pour pouvoir générer votre attestation.'
          }
        </p>
        {lastDiagId && (
          <Button
            onClick={() => navigate(`/diagnostic/${lastDiagId}/attestation`)}
            size="lg"
          >
            <Award className="size-4 mr-1.5" />
            Générer mon attestation
          </Button>
        )}
      </div>
    </div>
  )
}
